import React, { useState } from 'react';
import { customToast } from '../utils/toastUtils';
import { useUser } from '../contexts/UserContext';
import './CustomerSupportModal.css';

const CustomerSupportModal = ({ show, onClose, selectedIssue, category }) => {
  const { userData } = useUser();
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userData?.uid) {
      customToast.info('Please login first to raise a support ticket');
      handleClose();
      return;
    }

    if (description.trim() === '') {
      customToast.warning('Please provide a detailed description of your issue');
      return;
    }

    setIsSubmitting(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/email/support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userData.uid,
          userEmail: userData?.email || 'Anonymous User',
          userName: userData?.name || userData?.displayName || userData?.email?.split('@')[0] || 'User',
          category: category,
          issue: selectedIssue,
          description: description.trim()
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        customToast.success('Support request submitted successfully!');
        
        // Reset and close after 2 seconds
        setTimeout(() => {
          setDescription('');
          setSubmitted(false);
          setIsSubmitting(false);
          onClose();
        }, 2500);
      } else {
        throw new Error(result.error || 'Failed to submit support request');
      }

    } catch (error) {
      console.error('Error submitting support request:', error);
      if (error.name === 'AbortError') {
        customToast.error('Request timed out. Please check your connection and try again.');
      } else {
        customToast.error('Failed to submit support request. Please try again or email us directly at support@resumeglobe.com');
      }
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setDescription('');
      setSubmitted(false);
      onClose();
    }
  };

  const charCount = description.length;
  const maxChars = 2000;

  return (
    <div className="support-modal-overlay" onClick={handleClose}>
      <div className="support-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="support-modal-close" onClick={handleClose} disabled={isSubmitting}>
          ×
        </button>

        {!submitted ? (
          <>
            <div className="support-modal-header">
              <div className="support-modal-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 7v6M12 17h.01" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2>Submit Support Request</h2>
              <p>We'll get back to you as soon as possible</p>
            </div>

            <form onSubmit={handleSubmit} className="support-form">
              <div className="support-category-display">
                <div className="category-badge">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 2v6h6V2M9 16v6h6v-6M3 9h6v6H3M15 9h6v6h-6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {category}
                </div>
              </div>

              <div className="support-issue-section">
                <label className="support-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 8v4M12 16h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Your Issue
                </label>
                <div className="selected-issue-box">
                  {selectedIssue}
                </div>
              </div>

              <div className="support-description-section">
                <label className="support-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Detailed Description
                </label>
                <textarea
                  className="support-textarea"
                  placeholder="Please describe your issue in detail. Include any error messages, screenshots description, or steps to reproduce the problem..."
                  value={description}
                  onChange={(e) => {
                    if (e.target.value.length <= maxChars) {
                      setDescription(e.target.value);
                    }
                  }}
                  rows="8"
                  required
                  disabled={isSubmitting}
                />
                <div className="support-counter">
                  <span className={charCount > maxChars * 0.9 ? 'warning' : ''}>
                    {charCount}/{maxChars} characters
                  </span>
                </div>
              </div>

              <div className="support-user-info">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M8.5 11a4 4 0 100-8 4 4 0 000 8z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Your email: <strong>{userData?.email || 'Not logged in'}</strong></span>
              </div>

              <button 
                type="submit" 
                className="support-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Submit Support Request
                  </>
                )}
              </button>

              <p className="support-note">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4M12 8h.01"/>
                </svg>
                We typically respond within 24-48 hours during business hours (Mon-Fri, 10 AM - 6 PM IST)
              </p>
            </form>
          </>
        ) : (
          <div className="support-success">
            <div className="success-icon">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01l-3-3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Request Submitted!</h2>
            <p>Thank you for contacting our support team.</p>
            <p className="success-subtext">
              We've received your request and will get back to you at <strong>{userData?.email}</strong> as soon as possible.
            </p>
            <div className="success-reference">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              A confirmation will be sent to your email
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerSupportModal;
